// Service for checking template usage in matters
import { API_CONFIG } from '../config/api.config';

export class TemplateMatterService {
  private static baseUrl = API_CONFIG.BASE_URL;
  private static matterConfigBasePath = '/api/v1/admin/firm-settings/matter-configuration';

  private static getAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  }

  private static getAuthToken(): string | null {
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

    const keycloakToken = (window as any)?.keycloak?.token;
    if (keycloakToken && jwtPattern.test(keycloakToken)) {
      return keycloakToken;
    }

    const knownKeys = ['webelieve_access_token', 'access_token', 'token'];
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of knownKeys) {
        const directToken = storage.getItem(key);
        if (directToken && jwtPattern.test(directToken)) {
          return directToken;
        }
      }

      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) {
          continue;
        }

        if (jwtPattern.test(value)) {
          return value;
        }

        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenCandidate = parsed?.token || parsed?.access_token || parsed?.accessToken;
            if (tokenCandidate && jwtPattern.test(tokenCandidate)) {
              return tokenCandidate;
            }
          } catch {
            // Ignore non-JSON storage values.
          }
        }
      }
    }

    return null;
  }

  /**
   * Fetch authoritative template usage count from backend.
   */
  private static async fetchTemplateMatterCount(templateId: string): Promise<number> {
    const response = await fetch(
      `${this.baseUrl}${this.matterConfigBasePath}/custom-templates/${encodeURIComponent(templateId)}/matter-count`,
      {
        method: 'GET',
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to fetch template usage count (${response.status}): ${body}`);
    }

    const data = await response.json();
    return Number(data?.matterCount || 0);
  }

  /**
   * Get the count of matters using a specific template
   */
  static async getTemplateMatterCount(templateId: string): Promise<number> {
    try {
      return await this.fetchTemplateMatterCount(templateId);
    } catch (error) {
      console.error(`Error fetching matter count for template ${templateId}:`, error);
      return 0;
    }
  }

  /**
   * Get matter counts for multiple templates (optimized - single API call)
   */
  static async getTemplateMatterCounts(templateIds: string[]): Promise<Record<string, number>> {
    try {
      if (templateIds.length === 0) {
        return {};
      }

      const counts: Record<string, number> = {};
      
      // Initialize all counts to 0
      templateIds.forEach(id => {
        counts[id] = 0;
      });

      const entries = await Promise.all(
        templateIds.map(async (templateId) => [templateId, await this.fetchTemplateMatterCount(templateId)] as const)
      );

      entries.forEach(([templateId, count]) => {
        counts[templateId] = count;
      });

      return counts;
    } catch (error) {
      console.error('Error fetching template matter counts:', error);
      return {};
    }
  }

  /**
   * Check if a template can be deleted (no active matters using it)
   */
  static async canDeleteTemplate(templateId: string): Promise<{ canDelete: boolean; matterCount: number }> {
    try {
      const count = await this.getTemplateMatterCount(templateId);
      return {
        canDelete: count === 0,
        matterCount: count
      };
    } catch (error) {
      console.error(`Error checking if template ${templateId} can be deleted:`, error);
      return {
        canDelete: false,
        matterCount: 0
      };
    }
  }

  /**
   * Check if a template can be fully edited (pattern changes allowed)
   */
  static async canEditTemplatePattern(templateId: string): Promise<{ canEdit: boolean; matterCount: number }> {
    try {
      const count = await this.getTemplateMatterCount(templateId);
      return {
        canEdit: count === 0,
        matterCount: count
      };
    } catch (error) {
      console.error(`Error checking if template ${templateId} pattern can be edited:`, error);
      return {
        canEdit: false,
        matterCount: 0
      };
    }
  }

  static async deleteTemplate(templateId: string): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}${this.matterConfigBasePath}/custom-templates/${encodeURIComponent(templateId)}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to delete template (${response.status}): ${body}`);
    }
  }
} 
